/* V4 header behaviour. Ported from wireNavigation() in
   serious-readers-brand-2026/public/navigation.js.

   The prototype's search matching is gone: Dawn's <predictive-search> element
   queries the real store instead. What remains is the mobile toggle, the
   one-panel-at-a-time disclosure rule and the Escape handling. */
(function () {
  // True from a press of a finger, pen or mouse until just after it lifts, so the menus can tell a tap from a Tab key.
  var pointerDown = false;
  document.addEventListener('pointerdown', function () { pointerDown = true; }, true);
  document.addEventListener('pointerup', function () { setTimeout(function () { pointerDown = false; }, 0); }, true);
  document.addEventListener('pointercancel', function () { pointerDown = false; }, true);

  function wire(header) {
    if (header.dataset.navReady === 'true') return;
    header.dataset.navReady = 'true';

    var toggle = header.querySelector('.sr-mobile-toggle');
    var disclosures = header.querySelectorAll('.sr-disclosure');
    var searchInput = header.querySelector('#v4-search-input');

    if (toggle) {
      toggle.addEventListener('click', function () {
        var expanded = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(expanded));
        header.classList.toggle('sr-mobile-open', expanded);
      });
    }

    // Opening one panel closes the others.
    disclosures.forEach(function (detail) {
      detail.addEventListener('toggle', function () {
        if (!detail.open) return;
        disclosures.forEach(function (other) {
          if (other !== detail) other.open = false;
        });
        if (detail.classList.contains('sr-search') && searchInput) searchInput.focus();
      });
      // Tabbing out of a menu closes it, so it never covers the next thing the keyboard reaches (launch audit, 25 Sep
      // 2026). Keyboard only: a tap or click also moves the focus, and closing the menu then shifted the page under the
      // finger and lost the tap on the next menu (launch audit recheck, 25 Sep 2026). Taps and clicks keep the existing
      // one-panel-at-a-time and click-away rules.
      if (!detail.classList.contains('sr-search')) {
        detail.addEventListener('focusout', function (event) {
          if (pointerDown) return;
          if (event.relatedTarget && !detail.contains(event.relatedTarget)) detail.open = false;
        });
      }
    });

    header.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var active = event.target.closest('.sr-disclosure');
      disclosures.forEach(function (d) {
        d.open = false;
      });
      if (header.classList.contains('sr-mobile-open')) {
        header.classList.remove('sr-mobile-open');
        if (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
          toggle.focus();
        }
      } else if (active) {
        var summary = active.querySelector('summary');
        if (summary) summary.focus();
      }
    });

    // THE SEARCH RESULTS FIT WHAT YOU CAN SEE (Josh, 25 Sep 2026: "if you type into search, you cannot then scroll
    // down"). While search is open, the panel's height is the room between its top and the bottom of the visible
    // screen, which on a phone is the top of the keyboard, so every result can be scrolled into view. The CSS keeps the
    // old whole-screen height for any browser without the visual viewport. Styles: assets/v4-navigation.css.
    var searchDetail = header.querySelector('.sr-search');
    var searchPanel = header.querySelector('.sr-search-panel');
    function fitSearch() {
      if (!searchDetail || !searchPanel || !searchDetail.open) return;
      var view = window.visualViewport;
      var visibleBottom = view ? view.offsetTop + view.height : window.innerHeight;
      var room = Math.floor(visibleBottom - searchPanel.getBoundingClientRect().top - 8);
      searchPanel.style.setProperty('--sr-search-room', Math.max(160, room) + 'px');
    }
    if (searchDetail && searchPanel) {
      searchDetail.addEventListener('toggle', fitSearch);
      if (searchInput) {
        searchInput.addEventListener('focus', fitSearch);
        searchInput.addEventListener('input', fitSearch);
      }
      window.addEventListener('resize', fitSearch);
      window.addEventListener('scroll', fitSearch, { passive: true });
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', fitSearch);
        window.visualViewport.addEventListener('scroll', fitSearch);
      }
    }

    // Clicking away closes any open panel.
    document.addEventListener('click', function (event) {
      if (header.contains(event.target)) return;
      disclosures.forEach(function (d) {
        d.open = false;
      });
    });

    // Escape closes the phone menu wherever the focus is. Safari does not focus a button when it is tapped, so the
    // header's own Escape handler above never heard it (launch audit, 25 Sep 2026).
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || header.contains(event.target) || !header.classList.contains('sr-mobile-open')) return;
      header.classList.remove('sr-mobile-open');
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      }
    });

    // A page brought back by the Back button comes back with its menus shut (launch audit, 25 Sep 2026).
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      disclosures.forEach(function (d) {
        d.open = false;
      });
      header.classList.remove('sr-mobile-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }

  function init() {
    document.querySelectorAll('[data-v4-header]').forEach(wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', init);
})();

/* SAME-PAGE JUMPS (launch audit, 25 Sep 2026). Many buttons are written as the page's own address plus a #part, such as
   "/collections/floor-lights#compare". When the visitor's address carries anything after a "?" (an ad's gclid, a
   ?code=), the browser treated that as a different page and reloaded it, which also dropped display-only codes and
   their offer prices. A link to this same page, with no query of its own or the same one, now just jumps to its part
   and keeps the visitor's address as it is. Anything else (another page, new tab, modifier keys) is left alone.
   It listens on window, so it runs after every other click handler: a link another script owns (the chooser's
   "#chooser" opener, which stops the jump itself) is left to that script (launch audit recheck, 25 Sep 2026). */
(function () {
  window.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target && event.target.closest ? event.target.closest('a[href*="#"]') : null;
    if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;
    var url;
    try { url = new URL(link.getAttribute('href'), window.location.href); } catch (e) { return; }
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.hash.length < 2) return;
    // Only the one case the browser gets wrong: the link carries no query while the address does, which it treats as a
    // different page and reloads. Every other same-page link (a bare "#part", the skip link, the same query) is left
    // to the browser, which already jumps in place and moves the keyboard focus (launch audit recheck, 25 Sep 2026).
    if (url.search || !window.location.search) return;
    var id;
    try { id = decodeURIComponent(url.hash.slice(1)); } catch (e) { return; }
    var target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    if (window.location.hash !== url.hash) {
      window.location.hash = url.hash;
    } else {
      // The same #part again moves nothing by itself: scroll to it, and move the keyboard there as the browser would.
      target.scrollIntoView();
      if (!target.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA|SUMMARY)$/.test(target.tagName)) target.setAttribute('tabindex', '-1');
      try { target.focus({ preventScroll: true }); } catch (e) {}
    }
  });
})();
